import { useState } from "react";
import { FaSitemap } from "react-icons/fa";
import Modal from "../../components/modal/modal";
import Dropdown from "../../components/modal/dropdown";
import { toast } from "react-toastify";
import { createDesignation, updateDesignation } from "../services/designationservice";
import { getDesignationStatusLabel, getDesignationStatusClass } from "../../constants/constants";

const DesignationFormModal = ({ editingDesignation, deptOptions, existingDesignations, onClose, onSaved }) => {
  const [selectedDept, setSelectedDept] = useState(editingDesignation?.department || "");
  const [inputValue, setInputValue] = useState(editingDesignation?.name || "");
  const [status, setStatus] = useState(editingDesignation?.is_active ?? true);

  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    const trimmedName = inputValue.trim();
    const newErrors = {};

    if (!selectedDept) newErrors.dept = "Please select a department";
    if (!trimmedName) newErrors.name = "Designation name is required";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    /* Duplicate check against the currently loaded list */
    const duplicate = existingDesignations.find(
      (d) =>
        d.name.toLowerCase() === trimmedName.toLowerCase() &&
        d.department === selectedDept &&
        d.id !== editingDesignation?.id
    );

    if (duplicate) {
      setErrors({ name: "Designation already exists in this department" });
      return;
    }

    try {
      setSaving(true);
      await new Promise((res) => setTimeout(res, 300));

      if (editingDesignation) {
        await updateDesignation(editingDesignation.id, {
          name: trimmedName,
          department: selectedDept,
          is_active: status,
        });
        toast.success("Designation updated successfully");
      } else {
        await createDesignation({
          name: trimmedName,
          department: selectedDept,
          is_active: status,
        });
        toast.success("Designation added successfully");
      }

      onSaved();
    } catch (err) {
      const respData = err.response?.data;
      const backendErrors = respData?.errors || (respData && typeof respData === "object" && !respData.message && !respData.detail ? respData : null);
      if (backendErrors && typeof backendErrors === "object") {
        const formatted = {};
        for (const key in backendErrors) {
          const val = backendErrors[key];
          formatted[key] = Array.isArray(val) ? val[0] : val;
        }
        if (formatted.department) formatted.dept = formatted.department;
        setErrors((prev) => ({ ...prev, ...formatted }));
      }
      toast.error(err.response?.data?.message || err.response?.data?.detail || "Failed to save designation");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={editingDesignation ? "Edit Designation" : "Add Designation"} onClose={() => { if (!saving) onClose(); }}>
      {errors.non_field_errors && <div className="form-error">{errors.non_field_errors}</div>}

      <div className="employee-form">
        <div className="employee-form-scroll">
          <div className="form-section-card">
            <div className="section-title-premium">
              <FaSitemap /> Designation Information
            </div>

            <div className="grid-1">
              <div>
                <Dropdown
                  options={deptOptions}
                  value={selectedDept}
                  placeholder="Select Department *"
                  onChange={(val) => {
                    setSelectedDept(val);
                    setErrors((prev) => ({ ...prev, dept: "" }));
                  }}
                />
                {errors.dept && <div className="field-error">{errors.dept}</div>}
              </div>

              <div>
                <input
                  className={errors.name ? "input-error" : ""}
                  placeholder="Designation Name *"
                  value={inputValue}
                  autoFocus
                  onChange={(e) => {
                    setInputValue(e.target.value);
                    setErrors((prev) => ({ ...prev, name: "" }));
                  }}
                  onKeyDown={(e) => e.key === "Enter" && handleSave()}
                />
                {errors.name && <div className="field-error">{errors.name}</div>}
              </div>

              {/* STATUS TOGGLE — same pattern as Department */}
              <div className="status-toggle">
                <span className={`status-label ${getDesignationStatusClass(status)}`}>
                  {getDesignationStatusLabel(status)}
                </span>
                <div className={`toggle-switch ${status ? "active" : ""}`} onClick={() => setStatus(!status)}>
                  <div className="toggle-knob"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="modal-footer">
        <div className="modal-actions">
          <button className="primary" onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </button>
          <button className="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default DesignationFormModal;